import React, { useEffect, useState } from "react";
import { useUnsavedChanges } from "./UnsavedChangesContext";

function formatYMD(value) {
  if (!value) return "";
  const str = value.toString();
  return str.length >= 10 ? str.slice(0, 10) : str;
}

function ActivityCard({ item, isAdmin, onSaved }) {
  const { setUnsavedChanges } = useUnsavedChanges();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(item.name || "");
  const [costPerson, setCostPerson] = useState(item.addedCostPerPerson ?? "");
  const [costActivity, setCostActivity] = useState(item.addedCostPerActivity ?? "");
  const [notes, setNotes] = useState(item.extraNotes || "");
  const [availableDate, setAvailableDate] = useState(item.availableDate || "");
  const [endDate, setEndDate] = useState(item.endDate || "");
  const [url, setUrl] = useState(item.url || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setName(item.name || "");
      setCostPerson(item.addedCostPerPerson ?? "");
      setCostActivity(item.addedCostPerActivity ?? "");
      setNotes(item.extraNotes || "");
      setAvailableDate(formatYMD(item.availableDate || ""));
      setEndDate(formatYMD(item.endDate || ""));
      setUrl(item.url || "");
      setUnsavedChanges(false, null);
    }
  }, [item, isEditing]);

  useEffect(() => {
    if (!isEditing) {
      setUnsavedChanges(false, null);
      return;
    }
    const hasChanges =
      name !== (item.name || "") ||
      String(costPerson ?? "") !== String(item.addedCostPerPerson ?? "") ||
      String(costActivity ?? "") !== String(item.addedCostPerActivity ?? "") ||
      notes !== (item.extraNotes || "") ||
      availableDate !== formatYMD(item.availableDate || "") ||
      endDate !== formatYMD(item.endDate || "") ||
      url !== (item.url || "");
    setUnsavedChanges(hasChanges, save);
  }, [isEditing, name, costPerson, costActivity, notes, availableDate, endDate, url, item]);

  async function save() {
    if (!name.trim()) {
      alert("Name is required.");
      return false;
    }
    setSaving(true);
    try {
      const res = await fetch(`/api/activities/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          addedCostPerPerson: costPerson === "" ? null : Number(costPerson),
          addedCostPerActivity: costActivity === "" ? null : Number(costActivity),
          extraNotes: notes,
          availableDate: availableDate || null,
          endDate: endDate || null,
          url,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        alert(json?.error || "Failed to save activity");
        return false;
      }
      setIsEditing(false);
      setUnsavedChanges(false, null);
      onSaved?.();
      return true;
    } finally {
      setSaving(false);
    }
  }

  function cancelEdit() {
    setIsEditing(false);
    setUnsavedChanges(false, null);
  }

  return (
    <div
      style={{
        border: "1px solid var(--stroke)",
        borderRadius: 12,
        padding: 14,
        background: "var(--card)",
        boxShadow: "0 10px 24px rgba(32, 65, 81, 0.12)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <div style={{ fontWeight: 800, fontSize: 16 }}>{item.name}</div>
        {isAdmin && (
          <div style={{ display: "flex", gap: 8 }}>
            {!isEditing ? (
              <button onClick={() => setIsEditing(true)}>Edit</button>
            ) : (
              <>
                <button onClick={save} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </button>
                <button onClick={cancelEdit} disabled={saving}>
                  Cancel
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {isEditing ? (
        <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontWeight: 700 }}>Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontWeight: 700 }}>Added Cost Per Person</span>
              <input
                type="number"
                step="0.01"
                value={costPerson}
                onChange={(e) => setCostPerson(e.target.value)}
              />
            </label>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontWeight: 700 }}>Added Cost Per Activity</span>
              <input
                type="number"
                step="0.01"
                value={costActivity}
                onChange={(e) => setCostActivity(e.target.value)}
              />
            </label>
          </div>
          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontWeight: 700 }}>Extra Notes</span>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontWeight: 700 }}>
                Start Date <span style={{ fontWeight: 400, fontSize: 12, opacity: 0.7 }}>(leave blank if year round)</span>
              </span>
              <input
                type="date"
                value={availableDate}
                onChange={(e) => setAvailableDate(e.target.value)}
              />
            </label>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontWeight: 700 }}>
                End Date <span style={{ fontWeight: 400, fontSize: 12, opacity: 0.7 }}>(leave blank if year round)</span>
              </span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </label>
          </div>
          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontWeight: 700 }}>Activity URL</span>
            <input value={url} onChange={(e) => setUrl(e.target.value)} />
          </label>
        </div>
      ) : (
        <div style={{ marginTop: 10, display: "grid", gap: 6, fontSize: 14 }}>
          <div>Added Cost Per Person: {item.addedCostPerPerson != null ? `$${item.addedCostPerPerson}` : "—"}</div>
          <div>Added Cost Per Activity: {item.addedCostPerActivity != null ? `$${item.addedCostPerActivity}` : "—"}</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div>Start Date: {formatYMD(item.availableDate) || "—"}</div>
            <div>End Date: {formatYMD(item.endDate) || "—"}</div>
          </div>
          {item.extraNotes ? <div>Notes: {item.extraNotes}</div> : null}
          {item.url ? (
            <div>
              <a href={item.url} target="_blank" rel="noreferrer">
                {item.url}
              </a>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function ActivitiesPage({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [newName, setNewName] = useState("");
  const [newCostPerson, setNewCostPerson] = useState("");
  const [newCostActivity, setNewCostActivity] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [newAvailableDate, setNewAvailableDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/activities");
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to load activities");
      setItems(Array.isArray(json) ? json : []);
    } catch (e) {
      setError(e.message || "Failed to load activities");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function createActivity() {
    if (!isAdmin) return;
    if (!newName.trim()) {
      alert("Name is required.");
      return;
    }
    setCreating(true);
    try {
      const res = await fetch("/api/activities", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newName.trim(),
          addedCostPerPerson: newCostPerson === "" ? null : Number(newCostPerson),
          addedCostPerActivity: newCostActivity === "" ? null : Number(newCostActivity),
          extraNotes: newNotes,
          availableDate: newAvailableDate || null,
          endDate: newEndDate || null,
          url: newUrl,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        alert(json?.error || "Failed to create activity");
        return;
      }
      setNewName("");
      setNewCostPerson("");
      setNewCostActivity("");
      setNewNotes("");
      setNewAvailableDate("");
      setNewEndDate("");
      setNewUrl("");
      await load();
    } finally {
      setCreating(false);
    }
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
        <div>
          <h1 style={{ margin: 0 }}>Activities</h1>
          {!isAdmin && <div style={{ fontSize: 12, opacity: 0.7 }}>Read-only mode</div>}
        </div>
      </div>

      <div style={{ marginTop: 14, border: "1px solid #ddd", borderRadius: 10, padding: 12, opacity: isAdmin ? 1 : 0.6 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
          <div style={{ fontWeight: 800 }}>Add Activity</div>
          <button onClick={() => setAddOpen((v) => !v)} disabled={!isAdmin}>
            {addOpen ? "Hide" : "Show"}
          </button>
        </div>
        {addOpen && (
          <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
            <input
              placeholder="Activity name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={!isAdmin}
            />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <input
                type="number"
                step="0.01"
                placeholder="$ Added Cost Per Person"
                value={newCostPerson}
                onChange={(e) => setNewCostPerson(e.target.value)}
                disabled={!isAdmin}
              />
              <input
                type="number"
                step="0.01"
                placeholder="$ Added Cost Per Activity"
                value={newCostActivity}
                onChange={(e) => setNewCostActivity(e.target.value)}
                disabled={!isAdmin}
              />
            </div>
            <textarea
              rows={3}
              placeholder="Extra Notes"
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              disabled={!isAdmin}
            />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontWeight: 700 }}>
                  Start Date <span style={{ fontWeight: 400, fontSize: 12, opacity: 0.7 }}>(leave blank if year round)</span>
                </span>
                <input
                  type="date"
                  value={newAvailableDate}
                  onChange={(e) => setNewAvailableDate(e.target.value)}
                  disabled={!isAdmin}
                />
              </label>
              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontWeight: 700 }}>
                  End Date <span style={{ fontWeight: 400, fontSize: 12, opacity: 0.7 }}>(leave blank if year round)</span>
                </span>
                <input
                  type="date"
                  value={newEndDate}
                  onChange={(e) => setNewEndDate(e.target.value)}
                  disabled={!isAdmin}
                />
              </label>
            </div>
            <input
              placeholder="Activity URL"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              disabled={!isAdmin}
            />
            <div>
              <button onClick={createActivity} disabled={!isAdmin || creating}>
                {creating ? "Adding…" : "Add"}
              </button>
            </div>
          </div>
        )}
      </div>

      {loading && <div style={{ marginTop: 16 }}>Loading…</div>}
      {error && <div style={{ marginTop: 16, color: "crimson" }}>{error}</div>}

      {!loading && !error && (
        <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
          {items.map((item) => (
            <ActivityCard key={item.id} item={item} isAdmin={isAdmin} onSaved={load} />
          ))}
        </div>
      )}
    </div>
  );
}
